import { request } from "@shared/utils";
import { API, METHODS } from "@shared/constants";
import { RecognitionFilter } from "@shared/interfaces/RecognitionFilter.interface";
import {
  CreateRedemptionRequestDto,
  DenyRedemptionRequestDto,
  GetLeaderboardQuery,
  GetPendingRedemptionRequestsQuery,
  GetRedemptionLedgerQuery,
} from "@shared/models";
import { BulkApproveDto, CreateRecognitionDto, UpdateRecognitionDto } from "@containers/Recognition/interfaces";

export default {
  getRecognitions: (payload: RecognitionFilter) =>
    request(METHODS.GET, API.RECOGNITION.GET_RECOGNITIONS)({ params: payload }),
  getRecognition: (recognitionId: number) =>
    request(METHODS.GET, API.RECOGNITION.GET_RECOGNITION(String(recognitionId)))(),
  createRecognition: (payload: CreateRecognitionDto) =>
    request(METHODS.POST, API.RECOGNITION.CREATE_RECOGNITION)(payload),
  getCoreValues: () => request(METHODS.GET, API.RECOGNITION.GET_CORE_VALUES)(),
  updateRecognition: ({ id, ...rest }: UpdateRecognitionDto) =>
    request(METHODS.PUT, API.RECOGNITION.UPDATE_RECOGNITION(String(id)))(rest),
  // Body is the payload directly, not `{ params: payload }` -- that wrapper is only for GETs (see
  // getLeaderboard's own comment below); a POST's `request` call sends its argument straight through
  // as the request body.
  bulkApprove: (payload: BulkApproveDto) => request(METHODS.POST, API.RECOGNITION.BULK_APPROVE)(payload),
  getPendingCount: () => request(METHODS.GET, API.RECOGNITION.GET_PENDING_COUNT)(),
  getSparksSummary: (userId: number) => request(METHODS.GET, API.SPARKS.GET_SPARKS_SUMMARY(String(userId)))(),
  getUserBadges: (userId: number) => request(METHODS.GET, API.SPARKS.GET_USER_BADGES(String(userId)))(),
  getBadgeDefinitions: () => request(METHODS.GET, API.SPARKS.GET_BADGE_DEFINITIONS)(),
  // overview-rebuild-pr5-plan.md: a query-param endpoint (no userId path segment -- the api derives
  // the subject from the token), so this follows getLeaderboard's `{ params: ... }` precedent above,
  // not getSparksSummary's path-param style.
  getGivingActivity: (tzOffsetMinutes: number) =>
    request(METHODS.GET, API.SPARKS.GET_GIVING_ACTIVITY)({ params: { tzOffsetMinutes } }),
  // D5/§2 of 923-core-context context/engineering/spark-rewards/phase-38-implementation-plan.md: the `{ params: query }` wrapper is required, not
  // `(query)` directly -- `request`'s returned function passes its argument straight through as
  // axios's second (config) argument (shared/utils/request.tsx), so calling it bare would send no
  // query string at all and the api's `.strict()` discriminated union would 400 on the missing
  // `board`. Matches `getRecognitions`'s own `({ params: payload })` precedent above exactly.
  getLeaderboard: (query: GetLeaderboardQuery) => request(METHODS.GET, API.SPARKS.GET_LEADERBOARD)({ params: query }),
  // 923-core-context context/engineering/spark-rewards/phase-55-person-sparks-drawer.md PR1: restores the per-user read deleted in phase-50-w6a -- the
  // "a person's Sparks" drawer needs one person's counters/ledger/pendingRequests again, this time
  // keyed off a ledger-row click rather than a picker. Built from the restored GET_USER_REDEMPTION_SUMMARY
  // URL constant (admin/redemptions/:userId), distinct from GET_OWN_REDEMPTION_SUMMARY just below,
  // which is self-scoped and never admin-gated.
  getUserRedemptionSummary: (userId: number) =>
    request(METHODS.GET, API.SPARKS.GET_USER_REDEMPTION_SUMMARY(String(userId)))(),
  // redeem-tab-plan.md PR2: the Redeem tab's own read, built from the self-scoped
  // GET_OWN_REDEMPTION_SUMMARY URL constant.
  getOwnRedemptionSummary: (userId: number) =>
    request(METHODS.GET, API.SPARKS.GET_OWN_REDEMPTION_SUMMARY(String(userId)))(),
  // W1: the Redeem catalog, matching getBadgeDefinitions' own no-argument, unpaginated precedent above.
  getRewardItems: () => request(METHODS.GET, API.SPARKS.GET_REWARD_ITEMS)(),
  // 923-core-context context/engineering/spark-rewards/phase-49-w2-create-request.md: POSTs the employee-facing redemption-requests endpoint -- built
  // from a DISTINCT URL constant (CREATE_REDEMPTION_REQUEST), never the HR-gated admin/redemptions path
  // (that path's own write, createRedemption, was deleted alongside the mark-consumed flow in
  // 923-core-context context/engineering/spark-rewards/phase-49-w5-org-wide-ledger.md; admin/redemptions is now GET-only, read by getRedemptionLedger
  // below). Body is the payload directly, matching bulkApprove/createRecognition's own POST precedent
  // above.
  createRedemptionRequest: (payload: CreateRedemptionRequestDto) =>
    request(METHODS.POST, API.SPARKS.CREATE_REDEMPTION_REQUEST)(payload),
  // 923-core-context context/engineering/spark-rewards/phase-49-w3-pending-requests-panel.md: GetPendingRedemptionRequestsQuery.page is 0-indexed
  // everywhere in this app's Redux state (matching Chat's own getChatList precedent, api/api.tsx:7-11),
  // but the backend's admin/redemption-requests endpoint is 1-indexed (pageQueryNumber, api/app/shared/
  // schemas/shared.schemas.ts) -- translate only at this boundary.
  //
  // fix pass H: `status: "Pending"` is sent EXPLICITLY, not left to the api's own
  // `.optional().default(PENDING)` -- this panel only ever queries Pending rows (remainingIfApproved
  // depends on it being non-null, per RedemptionRequestQueueRow's own model comment), and a future
  // caller of this same query type must not silently inherit a different status by omission.
  getPendingRedemptionRequests: (payload: GetPendingRedemptionRequestsQuery) =>
    request(
      METHODS.GET,
      API.SPARKS.GET_REDEMPTION_REQUESTS,
    )({
      params: { ...payload, page: payload.page + 1, status: "Pending" },
    }),
  // 923-core-context context/engineering/spark-rewards/phase-49-w4-approve-deny.md: approve takes no body (architecture §5.5).
  approveRedemptionRequest: (id: number) => request(METHODS.POST, API.SPARKS.APPROVE_REDEMPTION_REQUEST(String(id)))(),
  // Body is `{ reason }` directly, matching bulkApprove/createRecognition's own POST precedent above --
  // `id` is destructured out, used only for the URL (architecture §5.6's REQUIRED reason).
  denyRedemptionRequest: ({ id, reason }: DenyRedemptionRequestDto) =>
    request(METHODS.POST, API.SPARKS.DENY_REDEMPTION_REQUEST(String(id)))({ reason }),
  // GetRedemptionLedgerQuery.page is 0-indexed in redux (matching every other pager in this app);
  // admin/redemptions is 1-indexed (pageQueryNumber) -- translate only here, mirroring
  // getPendingRedemptionRequests' own translation above. phase-50-w6a: no `user_id` filter any more --
  // this wrapper's `{ ...payload, page: ... }` spread has nothing else to pass through.
  getRedemptionLedger: (payload: GetRedemptionLedgerQuery) =>
    request(
      METHODS.GET,
      API.SPARKS.GET_REDEMPTION_LEDGER,
    )({
      params: { ...payload, page: payload.page + 1 },
    }),
  // phase-50-w6b: the three org-wide stat cards -- no payload, unpaginated, matching getRewardItems' own
  // no-argument precedent above.
  getRedemptionStats: () => request(METHODS.GET, API.SPARKS.GET_REDEMPTION_STATS)(),
  // 923-core-context context/engineering/spark-rewards/phase-52-spread-your-sparks.md §4 S3: viewer-scoped, no query params -- matching getRewardItems'/
  // getRedemptionStats' own no-argument precedent above.
  getLeaderboardCollaborators: () => request(METHODS.GET, API.SPARKS.GET_LEADERBOARD_COLLABORATORS)(),
  // 923-core-context context/engineering/spark-rewards/phase-51-leaderboard-to-design.md L8: viewer-scoped, no query params -- matching
  // getLeaderboardCollaborators' own no-argument precedent above. The card's OTHER figure this phase
  // adds (all-time rank) reuses getLeaderboard above rather than a second wrapper -- see
  // saga.tsx's getAllTimeRankSaga.
  getAllTimeSummary: () => request(METHODS.GET, API.SPARKS.GET_ALL_TIME_SUMMARY)(),
};
